import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PnRow } from "../../../../shared/phone/Row";
import { Icon, type IconName } from "../../../components/ui/Icon";
import type { GoalNumbers, HomeGoal, HomeProduct } from "../../../lib/ccApi";
import { initials } from "../../goal/text";
import { fmt, shortGoal, usd } from "../text";
import type { PaceLine } from "../v2";
import { paceOf } from "./numbers";
import type { Waiting } from "./waiting";

/** Home and Needs you on a phone and an iPad: the small parts their screens share. */

/** A product's mark: its logo, else its initials on the product's own square. */
export function ProductMark({ p, size = 32 }: { p: Pick<HomeProduct, "name" | "logoUrl">; size?: number }) {
  const style = { width: size, height: size, fontSize: Math.round(size * (initials(p.name).length > 1 ? 0.36 : 0.44)) };
  return p.logoUrl ? <img className="hm-mark" src={p.logoUrl} alt="" style={style} /> : <i className="hm-mark" style={style}>{initials(p.name)}</i>;
}

/** How many wait, in the small amber count the prototype puts beside a heading. */
export const Count = ({ n }: { n: number }) => <b className="hm-count">{n}</b>;

/** A goal's state as a pill, in the prototype's words. */
export function StatePill({ g, p }: { g: HomeGoal; p: PaceLine | null }) {
  const [tone, word] =
    g.state === "paused"
      ? ["warn", "Paused"]
      : g.state === "live"
        ? p?.kind === "behind"
          ? ["warn", "Behind"]
          : ["good", "On pace"]
        : g.state === "getting_ready"
          ? ["warn", "Getting ready"]
          : g.state === "plan_ready"
            ? ["violet", "Plan to review"]
            : g.state === "planning"
              ? ["violet", "Planning"]
              : g.state === "done"
                ? ["", "Finished"]
                : ["", "Setting up"];
  return (
    <span className={`pn-pill ${tone}`}>
      <i />
      {word}
    </span>
  );
}

/** So far in teal, where it lands at this rate striped, and a tick where it should be by today. */
function Pace({ p }: { p: PaceLine }) {
  const w = (v: number) => Math.max(0, Math.min(100, (100 * v) / Math.max(1, p.target)));
  return (
    <span className="hm-pbar" aria-hidden="true">
      <i className="g" style={{ width: `${p.kind === "done" ? w(p.got) : w(p.forecast)}%` }} />
      <i className="f" style={{ width: `${w(p.got)}%` }} />
      {p.kind !== "done" && <u style={{ left: `${w(p.expected)}%` }} />}
    </span>
  );
}

/** What a goal row says under its name: its count and cost each, its steps, or what it waits for. */
function goalMeta(g: HomeGoal, p: PaceLine | null, n: GoalNumbers | undefined): string {
  if (g.state === "getting_ready" && g.readyTotal) return `${g.readyTotal - (g.readyLeft ?? 0)} of ${g.readyTotal} steps done before launch`;
  if (g.state === "getting_ready") return "Getting ready to launch";
  if (g.state === "plan_ready") return "The plan is ready for you";
  if (g.state === "planning") return "The agent is writing the plan";
  if (g.state === "setting_up") return "Not set up yet";
  if (!p) return g.state === "done" ? "Finished" : "No results yet";
  const each = n && n.results > 0 ? ` · ${usd(Math.round(n.spendCents / n.results))} each` : "";
  return `${fmt(p.got)} of ${fmt(p.target)}${each}`;
}

/** A goal as a row: its state and what waits on it, its name, a thin pace bar and its numbers. */
export function GoalRow({ g, n, on = false }: { g: HomeGoal; n: GoalNumbers | undefined; on?: boolean }) {
  const p = paceOf(g, n);
  return (
    <PnRow
      to={`/goals/${encodeURIComponent(g.id)}`}
      className={on ? "hm-goal on" : "hm-goal"}
      title={
        <>
          <span className="hm-gtop">
            <StatePill g={g} p={p} />
            {g.needs > 0 && <Count n={g.needs} />}
          </span>
          <span className="hm-gname">{shortGoal(g.sentence)}</span>
          {p && g.state !== "done" && <Pace p={p} />}
        </>
      }
      sub={goalMeta(g, p, n)}
    />
  );
}

const TONE: Record<Waiting["kind"], "violet" | "warn" | undefined> = { need: undefined, plan: "violet", ready: "warn" };

/** The icon and its colour for something that waits, as the prototype draws each kind. */
export function waitIcon(w: Waiting): { icon: IconName; tone: "violet" | "warn" | undefined } {
  const k = w.need?.kind ?? "";
  if (k === "email.reply") return { icon: "mail", tone: undefined };
  if (k === "email.step") return { icon: "send", tone: undefined };
  if (k.startsWith("ad.") || k === "goal.change" || k === "audience.update" || k === "placements.set") return { icon: k === "ad.add" ? "film" : w.icon, tone: "violet" };
  if (k === "budget.set" || k.startsWith("campaign.")) return { icon: w.icon, tone: "warn" };
  return { icon: w.icon, tone: TONE[w.kind] };
}

/** One thing that waits, as a row: what it asks, and the goal or one line about it under that. */
export function WaitRow({ w, to, sub, on = false }: { w: Waiting; to: string; sub: string; on?: boolean }) {
  const { icon, tone } = waitIcon(w);
  return <PnRow to={to} className={on ? "hm-wait on" : "hm-wait"} icon={<Icon name={icon} />} iconTone={tone} title={w.title} sub={sub} wrap />;
}

/**
 * Closes the open kit sheet with its own slide down. Why: the kit's sheet runs its closing slide only from
 * its own dismiss (Escape, the scrim, a pull), so a button inside asks it the same way.
 */
export function closeSheet(): void {
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
}

/** The note after an action, at the top of the screen and above any sheet. */
export function useHmToast() {
  const [t, setT] = useState<{ msg: string; ok: boolean; on: boolean }>({ msg: "", ok: true, on: false });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const say = useCallback((msg: string, ok = true) => {
    setT({ msg, ok, on: true });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setT((x) => ({ ...x, on: false })), ok ? 2600 : 4200);
  }, []);
  const toast = createPortal(
    <div className={t.on ? "hm-toast on" : "hm-toast"} role="status" aria-live="polite">
      {t.msg && <Icon name={t.ok ? "check" : "info"} />}
      <span>{t.msg}</span>
    </div>,
    document.body,
  );
  return { toast, say };
}
